// src/modules/competitions/services/judgeService.ts

import { httpClient } from '../../../shared/services/httpClient';
import { API_ENDPOINTS } from '../../../shared/services/apiConfig';
import {
  JudgeTermsApiResponse,
  JudgeApplicationApiResponse,
  JudgeStatusApiResponse,
  JudgeEventStatusApiResponse,
  JudgeImageTypesApiResponse,
  JudgeSubmissionsApiResponse,
  SaveEvaluationApiResponse,
  MyEvaluationsApiResponse,
  ApplyAsJudgeData,
  ApplyToLevelsData,
  SaveEvaluationData,
  CriterionEvaluation,
  // ✅ NUEVOS TIPOS IMPORTADOS
  ParticipantsListApiResponse,
  ParticipantDetailsApiResponse,
  VideoEvaluationApiResponse,
  ParticipantDetails,
  VideoEvaluationCriterion
} from '../types/judge';
import { ApiResponse } from '../../../types/api';

class JudgeService {
  /**
   * Obtener términos y condiciones para jueces de un evento
   */
  async getTerms(eventId: number): Promise<JudgeTermsApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.TERMS, {
      event_id: eventId.toString()
    });
  }

  /**
   * Postular como juez a una categoría
   */
  async apply(data: ApplyAsJudgeData): Promise<JudgeApplicationApiResponse> {
    return httpClient.post<any>(API_ENDPOINTS.JUDGE.APPLY, data);
  }

  /**
   * Aceptar términos para ser juez en un evento
   */
  async acceptTerms(data: {
    event_id: number;
    terms_id: number;
  }): Promise<ApiResponse<{
    registration_id: number;
    event_id: number;
    registration_type: string;
    approval_status: string;
    message: string;
  }>> {
    return httpClient.post<any>(API_ENDPOINTS.JUDGE.ACCEPT_TERMS, data);
  }

  /**
   * Verificar si el usuario tiene registro como juez en un evento
   */
  async checkRegistration(eventId: number): Promise<ApiResponse<{
    has_registration: boolean;
    registration_id?: number;
    approval_status?: 'pending' | 'approved' | 'rejected';
    terms_accepted_at?: string;
    rejected_at?: string;
    rejection_reason?: string;
  }>> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.CHECK_REGISTRATION, {
      event_id: eventId.toString()
    });
  }

  /**
   * Obtener estado como juez en una categoría específica
   */
  async getStatus(categoryId: number): Promise<JudgeStatusApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.STATUS, {
      category_id: categoryId.toString()
    });
  }

  /**
   * Obtener estado como juez por evento (todas las categorías)
   */
  async getStatusByEvent(eventId: number): Promise<JudgeEventStatusApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.STATUS_BY_EVENT, {
      event_id: eventId.toString()
    });
  }

  /**
   * Postular a niveles específicos
   */
  async applyToLevels(data: ApplyToLevelsData): Promise<ApiResponse<null>> {
    return httpClient.post<null>(API_ENDPOINTS.JUDGE.APPLY_LEVEL, data);
  }

  /**
   * Obtener mis niveles aprobados como juez
   */
  async getMyLevels(): Promise<ApiResponse<any>> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.MY_LEVELS);
  }

  /**
   * Obtener tipos de imagen para evaluar
   */
  async getImageTypes(
    categoryId: number,
    level: string
  ): Promise<JudgeImageTypesApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.EVALUATIONS, {
      category_id: categoryId.toString(),
      level: level
    });
  }

  /**
   * Obtener imágenes a evaluar de un tipo específico
   */
  async getSubmissions(
    categoryId: number,
    level: string,
    imageTypeId: number
  ): Promise<JudgeSubmissionsApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.EVALUATIONS, {
      category_id: categoryId.toString(),
      level: level,
      image_type_id: imageTypeId.toString()
    });
  }

  /**
   * Guardar evaluación de una imagen
   */
  async saveEvaluation(data: SaveEvaluationData): Promise<SaveEvaluationApiResponse> {
    return httpClient.post<any>(API_ENDPOINTS.JUDGE.EVALUATE, data);
  }

  /**
   * Obtener historial de mis evaluaciones
   */
  async getMyEvaluations(filters?: {
    is_final?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<MyEvaluationsApiResponse> {
    const params: Record<string, string> = {};

    if (filters?.is_final !== undefined) {
      params.is_final = filters.is_final ? '1' : '0';
    }
    if (filters?.limit) params.limit = filters.limit.toString();
    if (filters?.offset) params.offset = filters.offset.toString();

    return httpClient.get<any>(API_ENDPOINTS.JUDGE.MY_EVALUATIONS, params);
  }

  // ========================================
  // ✅ NUEVOS MÉTODOS PARA COLLAGE DE PARTICIPANTES
  // ========================================

  async getParticipantsWithMedia(
  categoryId: number,
  level: string,
  statusFilter?: 'all' | 'pending' | 'evaluated'
): Promise<ApiResponse<{
  participants: any[];  // ✅ Temporal
  total: number;
  category_id: number;
  level: string;
  status_filter: string;
}>> {
    const params: Record<string, string> = {
      category_id: categoryId.toString(),
      level: level
    };

    if (statusFilter) {
      params.status = statusFilter;
    }

    return httpClient.get<any>(
      API_ENDPOINTS.JUDGE.PARTICIPANTS_WITH_MEDIA,
      params
    );
  }

  /**
   * Obtener lista de participantes con estadísticas
   */
  async getParticipantsList(
    categoryId: number,
    level: string,
    statusFilter?: 'all' | 'pending' | 'evaluated'
  ): Promise<ParticipantsListApiResponse> {
    const params: Record<string, string> = {
      category_id: categoryId.toString(),
      level: level
    };

    if (statusFilter) {
      params.status = statusFilter;
    }

    return httpClient.get<any>(API_ENDPOINTS.JUDGE.PARTICIPANTS_LIST, params);
  }

  /**
   * Obtener detalles completos de un participante (imágenes y videos)
   */
  async getParticipantDetails(
    participantId: number
  ): Promise<ParticipantDetailsApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.JUDGE.PARTICIPANT_DETAILS, {
      participant_id: participantId.toString()
    });
  }

  /**
 * Generar token temporal para video
 */
  async generateVideoToken(data: {
    participant_id: number;
    video_type_id: number;
    type: 'participant' | 'example';
  }): Promise<ApiResponse<{
    token: string;
    expires_in: number;
  }>> {
    return httpClient.post<any>(API_ENDPOINTS.COMPETITIONS.GENERATE_VIDEO_TOKEN, data);
  }


  // AGREGAR ESTE MÉTODO AL judgeService.ts existente

/**
 * Obtener videos para evaluar de un tipo específico (con evaluaciones existentes)
 */
async getVideoSubmissions(
  categoryId: number,
  level: string,
  videoTypeId: number
): Promise<ApiResponse<{
  video_type_id: number;
  video_type_name: string;
  criteria: VideoEvaluationCriterion[];
  submissions: Array<{
    submission_id: number;
    participant_code: string;
    video_url: string;
    video_type_id: number;
    video_type_name: string;
    submitted_at: string;
    duration_seconds: number;
    is_evaluated: boolean;
    my_evaluation?: {
      evaluation_id: number;
      submission_id: number;
      judge_id: number;
      criteria_evaluations: CriterionEvaluation[];
      total_score: number;
      is_final: boolean;
      evaluated_at: string;
    };
  }>;
}>> {
  return httpClient.get<any>(API_ENDPOINTS.JUDGE.VIDEO_EVALUATIONS, {
    category_id: categoryId.toString(),
    level: level,
    video_type_id: videoTypeId.toString()
  });
}
  /**
   * Guardar evaluación de un video
   */
  async saveVideoEvaluation(data: {
    video_submission_id: number;
    criteria_evaluations: CriterionEvaluation[];
    is_final: boolean;
  }): Promise<VideoEvaluationApiResponse> {
    return httpClient.post<any>(API_ENDPOINTS.JUDGE.VIDEO_EVALUATE, data);
  }

  // ========================================
  // MÉTODOS AUXILIARES
  // ========================================

  /**
   * Calcular progreso de evaluaciones
   */
  calculateProgress(evaluated: number, total: number): {
    percentage: number;
    completed: boolean;
    remaining: number;
  } {
    const percentage = total > 0 ? (evaluated / total) * 100 : 0;

    return {
      percentage: Math.round(percentage * 100) / 100,
      completed: evaluated >= total,
      remaining: Math.max(0, total - evaluated)
    };
  }

  /**
   * Validar puntaje de criterio
   */
  validateCriterionScore(
    score: number,
    minScore: number,
    maxScore: number
  ): boolean {
    return score >= minScore && score <= maxScore;
  }

  /**
   * Calcular puntaje total ponderado
   */
  calculateWeightedScore(
    evaluations: Array<{ score: number; weight: number }>
  ): number {
    const totalWeight = evaluations.reduce((sum, e) => sum + e.weight, 0);
    const weightedSum = evaluations.reduce(
      (sum, e) => sum + e.score * e.weight,
      0
    );

    return totalWeight > 0 ? weightedSum / totalWeight : 0;
  }
}

export const judgeService = new JudgeService();